import { vaultDataToHookForm } from '@onefootprint/core';
import type { CustomRequirementPage, VaultData } from '@onefootprint/request-types';
import { getOtherInputFormName, otherLabelValue } from './constants';
import type { CustomDisType } from './get-custom-data-dis';

// Dropdown and checkbox questions can only write custom fields (no identity DI accepts either
// config), so their keys resolve to VaultData's single `custom.*` index signature. Writing through a
// union of concrete `id.*` keys instead would narrow the assignable value type to their intersection.
type CustomFieldDi = `custom.${string}`;

type PromptSection = Extract<CustomRequirementPage['sections'][number], { kind: 'prompt' }>;
type AddressConfig = Extract<PromptSection['config'], { kind: 'address' }>;
type DropdownConfig = Extract<PromptSection['config'], { kind: 'dropdown' }>;

// Address sub-fields that need to be transformed from {di}.{field} to {di}_{field}
const ADDRESS_SUB_FIELDS = ['address_line1', 'address_line2', 'city', 'state', 'zip', 'country'];

/**
 * Handles address field defaults:
 * - Transforms vault keys from dot notation to underscore notation (Form.AddressLines format)
 *   e.g., "custom.address.address_line1" → "custom.address_address_line1"
 * - Sets default country to 'US' if not provided
 *
 * An identity address needs no key transform: its sub-fields are already their own dotted DIs
 * (`id.city`), which is the shape `Form.AddressLines` reads via its `__DOT__` vault separator.
 */
const handleAddressDefaults = (defaultValues: VaultData, di: string, _config: AddressConfig): VaultData => {
  const isIdentity = di.startsWith('id.');
  const dotKeyPrefix = `${di}.`;

  const transformed = Object.entries(defaultValues).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (!isIdentity && key.startsWith(dotKeyPrefix)) {
      const field = key.slice(dotKeyPrefix.length);
      if (ADDRESS_SUB_FIELDS.includes(field)) {
        acc[`${di}_${field}`] = value;
        return acc;
      }
    }
    acc[key] = value;
    return acc;
  }, {});

  const countryDi = (isIdentity ? 'id.country' : `${di}_country`) as CustomDisType;
  transformed[countryDi] = transformed[countryDi] ?? 'US';

  return transformed as VaultData;
};

/**
 * Handles dropdown field defaults:
 * - If "includeOther" is enabled and the current value is not in options,
 *   sets dropdown to "other" and stores the actual value in the other input field
 */
const handleDropdownDefaults = (
  defaultValues: VaultData,
  di: string,
  config: DropdownConfig,
  decryptedData: VaultData,
): VaultData => {
  const { options, includeOther } = config;
  const diKey = di as CustomFieldDi;
  const currentValue = decryptedData[diKey] as string;

  if (includeOther && currentValue && !options.includes(currentValue)) {
    // Value is not in options, so it was entered via "Other" input
    // Set dropdown to "other" and store the actual value in the other input field
    defaultValues[diKey] = otherLabelValue.value;
    const otherInputKey = getOtherInputFormName(diKey);
    defaultValues[otherInputKey as CustomFieldDi] = currentValue;
  }

  return defaultValues;
};

/**
 * Converts VaultData to VaultFormData for react-hook-form defaultValues
 * Handles special cases like address fields, dropdown "other" option, and value validation
 */
export const getDefaultValues = (
  decryptedVault: VaultData,
  sections: CustomRequirementPage['sections'],
  decryptedData: VaultData,
) => {
  let defaultValues = { ...decryptedVault };

  for (const section of sections) {
    if (section.kind !== 'prompt') {
      continue;
    }

    if (section.config.kind === 'address') {
      defaultValues = handleAddressDefaults(defaultValues, section.di, section.config);
    }

    if (section.config.kind === 'dropdown') {
      defaultValues = handleDropdownDefaults(defaultValues, section.di, section.config, decryptedData);
    }

    if (section.config.kind === 'checkbox') {
      const diKey = section.di as CustomFieldDi;
      defaultValues[diKey] = defaultValues[diKey] ?? false;
    }
  }

  return vaultDataToHookForm(defaultValues);
};
